/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

/**
 * IAdditionalContentData: Interface for
 * Active content.
 */
interface IAdditionalContentData {
    subtext?: string;
}

/**
 * IActiveImageAdditionalContentProps: Interface for
 * Active Image Additional Content Component props.
 */
export interface IActiveImageAdditionalContentProps {
    requestContext: Msdyn365.IRequestContext;
    additionalContent: IAdditionalContentData | undefined;
    handleAdditionalTextChange(event: Msdyn365.ContentEditableEvent): void;
    handleAdditionalParagraphChange(event: Msdyn365.ContentEditableEvent): void;
    handleAdditionalLinkTextChange(linkIndex: number, event: Msdyn365.ContentEditableEvent): void;
}

/**
 * IActiveImageAdditionalContentViewProps: Interface for
 * Active Image Additional Content view props.
 */
export interface IActiveImageAdditionalContentViewProps {
    additionalContentNode: INodeProps;
    text: React.ReactNode;
    additionalContentContainer: INodeProps;
    additionalContentItemLinks: INodeProps;
}

/**
 * Renders additional content heading.
 * @param additionalContentParagraphText - Additional content paragraph text.
 * @param props - Active Image Additional content component props.
 * @returns Paragraph Node.
 */
const renderParagraph = (additionalContentParagraphText: string, props: IActiveImageAdditionalContentProps) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentParagraphText}
            tag='p'
            className='msc-active-image__details__additional-content__paragraph'
            editProps={{
                /**
                 * On edit event for heading text change.
                 * @param event - Change event.
                 */
                onEdit: event => {
                    props.handleAdditionalTextChange(event);
                },
                requestContext
            }}
        />
    );
};

/**
 * Create node for each additonal content item.
 * @param item - Active Image additonal content item props.
 * @param props - Active Image Additional content component props.
 * @returns IActiveImageAdditionalContentItemViewProps.
 */
const assembleNode = (item: IAdditionalContentData, props: IActiveImageAdditionalContentProps): IActiveImageAdditionalContentViewProps => {
    const paragraphNode = item.subtext && renderParagraph(item.subtext, props);

    return {
        additionalContentNode: { className: 'msc-active-image__details__additional-content' },
        text: paragraphNode,
        additionalContentContainer: { className: 'msc-active-image__details__additional-content__container' },
        additionalContentItemLinks: { className: 'msc-active-image__details__additional__content__cta' }
    };
};

/**
 * ActiveImageAdditionalContent component.
 * @param props - Active Image Additional content component props.
 * @returns Active Image Additional content view props.
 */
export const activeImageAdditionalContent = (
    props: IActiveImageAdditionalContentProps
): IActiveImageAdditionalContentViewProps | undefined => {
    const { additionalContent } = props;
    if (!additionalContent?.subtext) {
        return undefined;
    }

    return assembleNode(additionalContent, props);
};

export default activeImageAdditionalContent;
